// Flip Force — when the spectator holds the phone screen-down on the
// /notes list for at least N milliseconds and then turns it face-up,
// we silently force-open a magician-chosen decoy note. While face-down
// the spectator can scroll and tap freely (the list behaves 100%
// normally); only the actual flip-up event overrides their navigation.
//
// Detection runs off `devicemotion.accelerationIncludingGravity.z`
// (W3C spec: +Z points OUT of the screen):
//   z near +9.8  ≈ screen pointing at the floor (face-down)
//   z near -9.8  ≈ screen pointing at the ceiling (face-up)
// Verified empirically on iOS Safari 18.7 in production.
//
// Up-to-three-stage sequence: the magician can configure a second and
// optional third force note that fire on the NEXT (and NEXT-NEXT) flip
// after the previous one landed. This lets each force build on the
// last (convincer → kicker → finale). After all configured stages have
// fired, the trick stays silent on any further flips until the magician
// opens Settings via the 3-tap escape gesture, which resets the cursor.

import { STORAGE } from "./storage";

const DEFAULT_MIN_MS = 2000;

export function getFlipForceConfig() {
  const enabled = localStorage.getItem(STORAGE.FLIP_FORCE_ENABLED) === "1";
  const targetId = localStorage.getItem(STORAGE.FLIP_FORCE_TARGET_ID) || "";
  const targetId2 = localStorage.getItem(STORAGE.FLIP_FORCE_TARGET_ID_2) || "";
  const targetId3 = localStorage.getItem(STORAGE.FLIP_FORCE_TARGET_ID_3) || "";
  const raw = localStorage.getItem(STORAGE.FLIP_FORCE_MIN_FACEDOWN_MS);
  const parsed = parseInt(raw || "", 10);
  const minFacedownMs = Number.isFinite(parsed)
    ? Math.max(500, Math.min(10000, parsed))
    : DEFAULT_MIN_MS;
  return { enabled, targetId, targetId2, targetId3, minFacedownMs };
}

export function saveFlipForceEnabled(on) {
  localStorage.setItem(STORAGE.FLIP_FORCE_ENABLED, on ? "1" : "0");
}

export function saveFlipForceTargetId(id) {
  localStorage.setItem(STORAGE.FLIP_FORCE_TARGET_ID, id || "");
}

export function saveFlipForceTargetId2(id) {
  localStorage.setItem(STORAGE.FLIP_FORCE_TARGET_ID_2, id || "");
}

export function saveFlipForceTargetId3(id) {
  localStorage.setItem(STORAGE.FLIP_FORCE_TARGET_ID_3, id || "");
}

export function saveFlipForceMinFacedownMs(ms) {
  const clamped = Math.max(500, Math.min(10000, Number(ms) || DEFAULT_MIN_MS));
  localStorage.setItem(STORAGE.FLIP_FORCE_MIN_FACEDOWN_MS, String(clamped));
}

// ---------------------------------------------------------------------------
// Stage cursor — the sequence is strictly Force 1 → Force 2 → Force 3 → stop.
// The cursor only resets when the magician performs the 3-tap escape gesture
// (see DecoyNotes.jsx `onHeaderTap` + the window-level top-strip handler).
// Values: "0" = next flip fires note 1, "1" = next flip fires note 2,
//         "2" = next flip fires note 3, "3" = no further forces until reset.
// ---------------------------------------------------------------------------
export function getFlipForceStage() {
  const raw = localStorage.getItem(STORAGE.FLIP_FORCE_STAGE);
  const n = parseInt(raw || "", 10);
  if (n === 1) return 1;
  if (n === 2) return 2;
  if (n === 3) return 3;
  return 0;
}

export function setFlipForceStage(n) {
  const clamped = n === 1 ? 1 : n === 2 ? 2 : n === 3 ? 3 : 0;
  localStorage.setItem(STORAGE.FLIP_FORCE_STAGE, String(clamped));
}

export function resetFlipForceStage() {
  setFlipForceStage(0);
}
